import { FieldDescription } from "@/ds/shadcn/components/ui/field";
import { useLogin } from "@/features/auth/api/useLogin";
import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { AuthHeaderIcon } from "@/features/auth/components/AuthHeaderIcon";
import { AuthHeaderSubtitle } from "@/features/auth/components/AuthHeaderSubtitle";
import { AuthHeaderTitle } from "@/features/auth/components/AuthHeaderTitle";
import { ModalMfa } from "@/features/auth/components/ModalMfa";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { useTitle } from "@/lib/common/useTitle";
import { Form } from "@/lib/form/components/Form";
import { useAppForm } from "@/lib/form/useAppForm";
import { useAuthState } from "@/lib/state";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Link, useNavigate } from "react-router";
import { z } from "zod";

export const PageLogin: FC = () => {
	useTitle("Login");

	const ref = useFocusOnLoad();
	const login = useLogin();
	const navigate = useNavigate();
	const [mfaModalVisible, setMfaModalVisible] = useState(false);
	const { state, dispatchAuthSetPostLoginRedirectURL } = useAuthState();

	const validators = useMemo(() => z.object({
		emailAddress: z.email("Please supply a valid email address"),
		password: z.string().min(8, "Your password is at least 8 characters long")
	}), []);

	const form = useAppForm({
		defaultValues: {
			emailAddress: "",
			password: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => login.mutateAsync({
			emailAddress: value.emailAddress,
			password: value.password,
			mfaCode: null
		})
	});

	useEffect(() => {
		if (!login.data) {
			return;
		}

		if (!login.data.body.emailVerified) {
			navigate("/login/email-verify");
			return;
		}

		if (!login.data.body.accessToken) {
			setMfaModalVisible(true);
			return;
		}

		if (state.postLoginRedirectURL) {
			dispatchAuthSetPostLoginRedirectURL(undefined);
			navigate(state.postLoginRedirectURL);
			return;
		}

		navigate("/dashboard");
	}, [login.data]);

	const onMfaConfirmed = useCallback((mfaCode: string) => {
		login.mutate({
			emailAddress: form.getFieldValue("emailAddress"),
			password: form.getFieldValue("password"),
			mfaCode: mfaCode
		});
	}, [form, login]);

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<>
			<AuthHeader>
				<AuthHeaderIcon />
				<AuthHeaderTitle>Fyendal's Collection</AuthHeaderTitle>
				<AuthHeaderSubtitle>Enter your email below to login to your account</AuthHeaderSubtitle>
			</AuthHeader>

			<Form onSubmit={onSubmit}>
				<form.AppField name="emailAddress">
					{
						field => <field.TextField label="Email Address" type="email" inputMode="email" ref={ref} required />
					}
				</form.AppField>
				<form.AppField name="password">
					{
						field => <field.TextField label="Password" type="password" required trailing={<Link to="forgot-password/initiate" className="text-sm underline-offset-4 hover:underline">Forgot your password?</Link>} />
					}
				</form.AppField>
				<ErrorDisplay error={login.error} />
				<form.AppForm>
					<form.SubmitButton label="Login" />
				</form.AppForm>
				<FieldDescription className="text-center">
					Don&apos;t have an account? <Link to="register" className="underline underline-offset-4">Sign up</Link>
				</FieldDescription>
			</Form>

			<ModalMfa open={mfaModalVisible} onOpenChange={setMfaModalVisible} isLoading={login.isPending} error={login.error} onConfirmed={onMfaConfirmed} />
		</>
	);
};
